import { PageOptions } from '@graphcommerce/framer-next-pages'
import { hygraphPageContent, HygraphPagesQuery } from '@graphcommerce/graphcms-ui'
import { mergeDeep } from '@graphcommerce/graphql'
import {
  AddProductsToCartFormProps,
  getProductStaticPaths,
  jsonLdProduct,
  jsonLdProductOffer,
  ProductPageMeta,
  productPageCategory,
} from '@graphcommerce/magento-product'
import { defaultConfigurableOptionsSelection } from '@graphcommerce/magento-product-configurable'
import { jsonLdProductReview } from '@graphcommerce/magento-review'
import { redirectOrNotFound, StoreConfigDocument } from '@graphcommerce/magento-store'
import { GetStaticProps, JsonLd, LayoutHeader, LayoutTitle } from '@graphcommerce/next-ui'
import { Container } from '@mui/material'
import { GetStaticPaths } from 'next'
import { useEffect } from 'react'
import { LayoutNavigation, LayoutNavigationProps } from '../../components'
import {
  CallADruggistDocument,
  CallADruggistQuery,
} from '../../components/Dialogs/MoreInformationDialog/CallADruggist.gql'
import { gtagViewItem } from '../../components/GoogleAnalytics/gtagViewItem/gtagViewItem'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { ProductPageBreadCrumbs } from '../../components/Product/ProductPageBreadCrumbs/ProductPageBreadCrumbs'
import { ProductPageExtraProducts } from '../../components/Product/ProductPageExtraProducts/ProductPageExtraProducts'
import { ProductPageProductInfo } from '../../components/Product/ProductPageProductInfo/ProductPageProductInfo'
import { UspsDocument, UspsQuery } from '../../components/Usps/Usps.gql'
import { ProductPage2Document, ProductPage2Query } from '../../graphql/ProductPage2.gql'
import {
  SaleCategoryProductsDocument,
  SaleCategoryProductsQuery,
} from '../../graphql/SaleCategoryProducts.gql'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'

type Props = HygraphPagesQuery &
  UspsQuery &
  ProductPage2Query &
  Pick<AddProductsToCartFormProps, 'defaultValues'> &
  CallADruggistQuery &
  SaleCategoryProductsQuery &
  Pick<AddProductsToCartFormProps, 'defaultValues'>

type RouteProps = { url: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

function ProductPage(props: Props) {
  const { products, relatedUpsells, usps, defaultValues, saleCategory, ...addToCartInfo } = props

  const product = mergeDeep(products, relatedUpsells)?.items?.[0]

  useEffect(() => gtagViewItem(product), [product])

  if (!product?.sku || !product.url_key || !product.name || !product.categories?.[0]?.name)
    return null

  return (
    <>
      <LayoutHeader floatingMd secondary>
        <LayoutTitle size='small' component='span'>
          {product.name}
        </LayoutTitle>
      </LayoutHeader>
      <JsonLd
        item={{
          '@context': 'https://schema.org',
          ...jsonLdProduct(product),
          ...jsonLdProductOffer(product),
          ...jsonLdProductReview(product),
        }}
      />
      <ProductPageMeta {...product} />
      <Container sx={{ px: { xs: 0, sm: 3, md: 4 } }}>
        <ProductPageBreadCrumbs product={product} />
        <ProductPageProductInfo
          product={product}
          {...addToCartInfo}
          defaultValues={defaultValues}
        />
      </Container>
      <ProductPageExtraProducts
        product={product}
        productsOnSale={saleCategory?.[0]?.products?.items}
      />
    </>
  )
}

ProductPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default ProductPage

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  if (import.meta.graphCommerce.legacyProductRoute) return { paths: [], fallback: false }
  if (process.env.NODE_ENV === 'development') return { paths: [], fallback: 'blocking' }

  const path = (locale: string) => getProductStaticPaths(graphqlSsrClient(locale), locale)
  const paths = (await Promise.all(locales.map(path))).flat(1)

  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ params, locale }) => {
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)

  const urlKey = params?.url ?? '??'

  const conf = client.query({ query: StoreConfigDocument })
  const productPage = staticClient.query({ query: ProductPage2Document, variables: { urlKey } })
  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  const product = productPage.then((pp) =>
    pp.data.products?.items?.find((p) => p?.url_key === urlKey),
  )

  const pages = hygraphPageContent(staticClient, 'product/global', product, true)
  if (!(await product)) return redirectOrNotFound(staticClient, conf, params, locale)

  const category = productPageCategory(await product)
  const up =
    category?.url_path && category?.name
      ? { href: `/${category.url_path}`, title: category.name }
      : { href: `/`, title: 'Home' }
  const usps = staticClient.query({ query: UspsDocument, fetchPolicy: 'cache-first' })

  const callADruggist = staticClient.query({ query: CallADruggistDocument })

  const sales = staticClient.query({
    query: SaleCategoryProductsDocument,
  })

  return {
    props: {
      ...defaultConfigurableOptionsSelection(urlKey, client, (await productPage).data),
      ...(await callADruggist).data,
      ...(await sales).data,
      ...(await layout).data,
      ...(await pages).data,
      ...(await usps).data,
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: 60 * 20,
  }
}
